import { DateTime } from "luxon";

import { CaseReportSignature, Clinic } from "pwapi";

import { CaseDetailState } from "shared/reducers/caseDetailReducer";
import { getDateTimefromTimestamp } from "shared/utils/dates";

import "./CaseReportHeader.css";

import PatientSexAge from "components/PatientSexAge";

import { getPhoneString } from "./utils";

type CaseReportHeaderProps = {
  caseDetailObject: CaseDetailState;
  isPrelimReport?: boolean;
  dateReported?: {
    timestamp: string;
    timezone?: string;
  };
  isUnsigned?: boolean;
  selectedSignature?: CaseReportSignature;
};

const CaseReportHeader = ({
  caseDetailObject,
  dateReported,
  isUnsigned,
  isPrelimReport = false,
  selectedSignature,
}: CaseReportHeaderProps) => {
  const getClinicLocation = (location?: Clinic["location"]) => {
    return location ? (
      <>
        {location.street}
        {location.street2 && <>, {location.street2}</>}
        <br />
        {location.city}, {location.state} {location.zipcode}
        <br />
      </>
    ) : (
      <></>
    );
  };

  // NOTE: caseDetailObject.patient?.address?.phone1 || caseDetailObject.patient?.address?.phone2
  // ... are not available in PatientSerializer
  const patientPhone =
    caseDetailObject.patient?.phone || caseDetailObject.patient?.mobile_phone;

  return (
    <header className="ReportHeader">
      <table className="ReportHeaderTable">
        <thead>
          <tr className="ReportHeaderTable__headers">
            <th>Patient Information</th>
            <th>Ordering Provider</th>
            <th>Specimen Information</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <div className="DualColumnGrid">
                <span>PATIENT:</span>
                <span>
                  {caseDetailObject.patient?.last_name?.trim().toUpperCase()},{" "}
                  {caseDetailObject.patient?.first_name?.trim().toUpperCase()}
                  {caseDetailObject.patient?.middle_initial && (
                    <>
                      {" "}
                      {caseDetailObject.patient?.middle_initial
                        ?.trim()
                        .toUpperCase()}
                    </>
                  )}
                </span>
                <span>DOB:</span>
                <span>
                  {caseDetailObject.patient?.date_of_birth
                    ? DateTime.fromFormat(
                        caseDetailObject.patient.date_of_birth,
                        "yyyy-MM-dd"
                      ).toFormat("MM/dd/yyyy")
                    : " "}
                </span>
                <span>SEX/AGE:</span>
                <span>
                  <PatientSexAge
                    patient={caseDetailObject.patient}
                    shouldDisplayDOB={false}
                    dateReported={dateReported}
                  />
                </span>
                {patientPhone && (
                  <>
                    <span>PHONE:</span>
                    <span>{getPhoneString(patientPhone)}</span>
                  </>
                )}
                {caseDetailObject.patient?.medical_record_number && (
                  <>
                    <span>PID:</span>
                    <span>
                      {caseDetailObject.patient?.medical_record_number}
                    </span>
                  </>
                )}
              </div>
            </td>
            <td>
              {caseDetailObject.ordering_doctor?.first_name}{" "}
              {caseDetailObject.ordering_doctor?.last_name}
              {caseDetailObject.ordering_doctor?.userprofile?.credentials && (
                <>
                  , {caseDetailObject.ordering_doctor?.userprofile?.credentials}
                </>
              )}
              {caseDetailObject.supervising_physician && (
                <>
                  <br />
                  Supervisor:{" "}
                  {caseDetailObject.supervising_physician?.first_name}{" "}
                  {caseDetailObject.supervising_physician?.last_name}
                  {caseDetailObject.supervising_physician?.userprofile
                    ?.credentials && (
                    <>
                      ,{" "}
                      {
                        caseDetailObject.supervising_physician?.userprofile
                          ?.credentials
                      }
                    </>
                  )}
                </>
              )}
              <br />
              {caseDetailObject.patient?.clinic?.name}
              <br />
              {getClinicLocation(caseDetailObject.patient?.clinic?.location)}
              {caseDetailObject.patient?.clinic?.location?.phone1 && (
                <>
                  PHONE:{" "}
                  {getPhoneString(
                    caseDetailObject.patient?.clinic?.location?.phone1 || ""
                  )}
                  <br />
                </>
              )}
              {caseDetailObject.patient?.clinic?.location?.fax && (
                <>
                  FAX:{" "}
                  {getPhoneString(
                    caseDetailObject.patient?.clinic?.location?.fax || ""
                  )}
                </>
              )}
            </td>
            <td>
              <div className="DualColumnGrid">
                <span>Accession #:</span>
                <span>{caseDetailObject.accession}</span>
                <span>Date Collected:</span>
                <span>
                  {caseDetailObject.collected_date ? (
                    getDateTimefromTimestamp(
                      caseDetailObject.collected_date,
                      caseDetailObject.collected_date_timezone || "UTC"
                    ).toLocaleString(DateTime.DATE_SHORT)
                  ) : (
                    <>&nbsp;</>
                  )}
                </span>
                <span>Date Received:</span>
                <span>
                  {caseDetailObject.received_date ? (
                    getDateTimefromTimestamp(
                      caseDetailObject.received_date,
                      caseDetailObject.received_date_timezone || "UTC"
                    ).toLocaleString(DateTime.DATE_SHORT)
                  ) : (
                    <>&nbsp;</>
                  )}
                </span>
                {!isPrelimReport && (
                  <>
                    <span>Date Reported:</span>
                    <span>
                      {isUnsigned && !caseDetailObject.is_screening ? (
                        DateTime.local({
                          zone:
                            selectedSignature?.clia_certificate?.location
                              ?.timezone || "UTC",
                        }).toLocaleString(DateTime.DATE_SHORT)
                      ) : dateReported ? (
                        getDateTimefromTimestamp(
                          dateReported.timestamp,
                          dateReported.timezone || "UTC"
                        ).toLocaleString(DateTime.DATE_SHORT)
                      ) : (
                        <>&nbsp;</>
                      )}
                    </span>
                  </>
                )}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </header>
  );
};

export default CaseReportHeader;
